/**
 * ImportButton Component
 *
 * Unified import button that shows a modal with import options:
 * - Upload CSV
 * - Import from Image
 * - Import from PDF
 *
 * Consolidates three separate import buttons into one clean interface.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { uploadCSV } from '../../services/portfolioApi';
import { uploadDocument } from '../../services/api/documentImportApi';
import { showSuccess, showError } from '../../utils/alert';
import { useTheme } from '../../contexts/ThemeContext';
import { useDocumentImportContext } from '../../contexts/DocumentImportContext';
import { useNavigation, type NavigationProp } from '@react-navigation/native';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

interface ImportButtonProps {
  onUploadComplete?: () => void;
  /**
   * Programmatic trigger for callers that mount the button hidden
   * (PortfolioTab, the Book screen's 经典导入 entry). 'menu' only opens the
   * options modal — the picker flows run from there.
   */
  triggerImport?: 'csv' | 'image' | 'pdf' | 'menu' | null;
}

type ImportType = 'csv' | 'image' | 'pdf';

export function ImportButton({ onUploadComplete, triggerImport }: ImportButtonProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const navigation = useNavigation<NavigationProp<PortfolioNavigatorParamList>>();
  const { startImport, canImport, isImporting } = useDocumentImportContext();

  const [showModal, setShowModal] = useState(false);

  const handleImport = () => {
    // Don't show modal if already importing
    if (!canImport) {
      return;
    }
    setShowModal(true);
  };

  const handleCSVImport = async () => {
    if (!canImport) return;

    try {
      // Pick document
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      // Validate file extension
      if (!file.name.endsWith('.csv')) {
        showError(t('portfolio.onlyCsvAllowed', { defaultValue: 'Only .csv files are allowed' }));
        return;
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size && file.size > maxSize) {
        showError(t('portfolio.fileSizeExceeds', { defaultValue: 'File size exceeds 10MB limit' }));
        return;
      }

      setShowModal(false);

      // Create AbortController for cancellation
      const controller = new AbortController();

      try {
        // For web, we need to fetch the file first
        if (file.uri) {
          if (file.uri.startsWith('file://') || file.uri.startsWith('/')) {
            // For native, DocumentPicker already gives us what we need
            await uploadCSV(file as any, controller.signal);
          } else {
            // For web, fetch the file first
            const response = await fetch(file.uri);
            const blob = await response.blob();
            const fileObj = new File([blob], file.name, { type: 'text/csv' });
            await uploadCSV(fileObj, controller.signal);
          }
        }

        showSuccess(
          t('portfolio.csvUploadSuccess', { defaultValue: 'CSV uploaded successfully!' })
        );
        onUploadComplete?.();
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          showError(
            error.message ||
              t('portfolio.failedToUploadCSV', { defaultValue: 'Failed to upload CSV' })
          );
        }
      }
    } catch (error: any) {
      console.error('CSV import error:', error);
      if (error.message !== 'Canceled') {
        showError(t('portfolio.failedToPickFile', { defaultValue: 'Failed to pick file' }));
      }
    }
  };

  const handleImageImport = async () => {
    if (!canImport) return;

    try {
      // Request camera roll permissions
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (!permissionResult.granted) {
        showError(
          t('portfolio.cameraRollPermission', {
            defaultValue: 'Camera roll permission is required',
          })
        );
        return;
      }

      // Pick multiple images
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.7, // Reduce quality slightly for faster uploads
        base64: true,
        allowsMultipleSelection: true, // Enable multi-selection
      });

      if (result.canceled) {
        return;
      }

      const assets = result.assets;

      // Validate max 10 images
      if (assets.length > 10) {
        showError(t('portfolio.maxImagesAllowed', { defaultValue: 'Maximum 10 images allowed' }));
        return;
      }

      // Validate each file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      for (const asset of assets) {
        if (asset.fileSize && asset.fileSize > maxSize) {
          showError(
            t('portfolio.imageExceedsLimit', {
              defaultValue: 'Image "{{filename}}" exceeds 10MB limit',
              filename: asset.fileName || '',
            })
          );
          return;
        }
      }

      setShowModal(false);

      // Upload all images sequentially
      const uploadResults = [];
      const failedImages: string[] = [];

      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];

        try {
          const base64Data = asset.base64;
          if (!base64Data) {
            throw new Error(
              t('portfolio.failedToGetImageData', { defaultValue: 'Failed to get image data' })
            );
          }

          // Upload each image separately
          const response = await uploadDocument(
            base64Data,
            'image',
            asset.fileName || `image_${i + 1}.jpg`
          );

          uploadResults.push(response);

          // Start tracking only the first image in global context
          // This prevents showing multiple loading bars
          if (i === 0) {
            startImport(response.import_id, asset.fileName, 'image');
          }
        } catch (error: any) {
          console.error(`Failed to upload image ${i + 1}:`, error);
          failedImages.push(asset.fileName || `Image ${i + 1}`);
        }
      }

      // Check if any images succeeded
      if (uploadResults.length === 0) {
        showError(t('portfolio.allImagesFailed', { defaultValue: 'All images failed to upload' }));
        return;
      }

      // Show success message - user will be notified when processing completes
      const imageCount = uploadResults.length;
      showSuccess(
        t('portfolio.processingImages', {
          defaultValue: 'Processing {{count}} image{{plural}}... You can continue browsing.',
          count: imageCount,
          plural: imageCount > 1 ? 's' : '',
        })
      );

      if (failedImages.length > 0) {
        console.warn(`Failed images: ${failedImages.join(', ')}`);
      }

      onUploadComplete?.();
    } catch (error: any) {
      console.error('Image import error:', error);
      if (error.message !== 'Canceled') {
        showError(t('portfolio.failedToPickImages', { defaultValue: 'Failed to pick images' }));
      }
    }
  };

  const handleCameraImport = async () => {
    if (!canImport) return;

    try {
      // Request camera permissions
      const permissionResult = await ImagePicker.requestCameraPermissionsAsync();

      if (!permissionResult.granted) {
        showError(
          t('portfolio.cameraPermissionRequired', { defaultValue: 'Camera permission is required' })
        );
        return;
      }

      // Take photo
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        quality: 0.7,
        base64: true,
      });

      if (result.canceled) {
        return;
      }

      const asset = result.assets[0];

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (asset.fileSize && asset.fileSize > maxSize) {
        showError(t('portfolio.photoExceedsLimit', { defaultValue: 'Photo exceeds 10MB limit' }));
        return;
      }

      setShowModal(false);

      try {
        const base64Data = asset.base64;
        if (!base64Data) {
          throw new Error(
            t('portfolio.failedToGetPhotoData', { defaultValue: 'Failed to get photo data' })
          );
        }

        // Upload photo
        const response = await uploadDocument(
          base64Data,
          'image',
          asset.fileName || 'camera_photo.jpg'
        );

        // Start tracking in global context
        startImport(response.import_id, asset.fileName, 'image');

        showSuccess(
          t('portfolio.processingPhoto', {
            defaultValue: 'Processing photo... You can continue browsing.',
          })
        );
        onUploadComplete?.();
      } catch (error: any) {
        showError(
          error.message ||
            t('portfolio.failedToUploadPhoto', { defaultValue: 'Failed to upload photo' })
        );
      }
    } catch (error: any) {
      console.error('Camera import error:', error);
      showError(t('portfolio.failedToTakePhoto', { defaultValue: 'Failed to take photo' }));
    }
  };

  const handlePDFImport = async () => {
    if (!canImport) return;

    try {
      // Pick PDF document
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      // Validate file size (25MB for PDF)
      const maxSize = 25 * 1024 * 1024;
      if (file.size && file.size > maxSize) {
        showError(
          t('portfolio.pdfSizeExceedsLimit', { defaultValue: 'File size exceeds 25MB limit' })
        );
        return;
      }

      setShowModal(false);

      try {
        // Read file as base64
        let base64Data: string;
        if (Platform.OS === 'web') {
          // Web: Use FileReader
          const fileReader = await fetch(file.uri);
          const blob = await fileReader.blob();
          base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              // Remove data URL prefix
              const base64 = result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } else {
          // Native: Use FileSystem to read file as base64
          const FileSystem = require('expo-file-system');
          const base64Result = await FileSystem.readAsStringAsync(file.uri, {
            encoding: FileSystem.EncodingType.Base64,
          });
          base64Data = base64Result;
        }

        // Upload PDF
        const response = await uploadDocument(base64Data, 'pdf', file.name);

        // Start tracking in global context
        startImport(response.import_id, file.name, 'pdf');

        showSuccess(
          t('portfolio.processingPdf', {
            defaultValue: 'Processing PDF... This may take a moment. You can continue browsing.',
          })
        );
        onUploadComplete?.();
      } catch (error: any) {
        showError(
          error.message ||
            t('portfolio.failedToUploadPdf', { defaultValue: 'Failed to upload PDF' })
        );
      }
    } catch (error: any) {
      console.error('PDF import error:', error);
      showError(t('portfolio.failedToPickPdf', { defaultValue: 'Failed to pick PDF' }));
    }
  };

  // Handle external trigger
  useEffect(() => {
    if (triggerImport === 'csv') {
      handleCSVImport();
    } else if (triggerImport === 'image') {
      handleImageImport();
    } else if (triggerImport === 'pdf') {
      handlePDFImport();
    } else if (triggerImport === 'menu') {
      setShowModal(true);
    }
  }, [triggerImport]);

  return (
    <>
      {/* Single Import Button */}
      <TouchableOpacity
        style={[
          styles.importButton,
          {
            backgroundColor: canImport ? theme.colors.primary : theme.colors.border,
            opacity: canImport ? 1 : 0.6,
          },
        ]}
        onPress={handleImport}
        disabled={!canImport}
      >
        <Text
          style={[
            styles.importButtonText,
            {
              color: canImport ? theme.colors.textInverse : theme.colors.textSecondary,
            },
          ]}
        >
          {isImporting
            ? t('portfolio.importing', { defaultValue: 'Importing...' })
            : t('portfolio.importPortfolioData', { defaultValue: 'Import Portfolio Data' })}
        </Text>
      </TouchableOpacity>

      {/* Import Options Modal */}
      <Modal
        visible={showModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowModal(false)}
        >
          <View style={[styles.modalContent, { backgroundColor: theme.colors.surface }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {t('portfolio.importPortfolioData', { defaultValue: 'Import Portfolio Data' })}
              </Text>
              <TouchableOpacity style={styles.closeButton} onPress={() => setShowModal(false)}>
                <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>
                  ✕
                </Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.optionsContainer}>
              {/* CSV Option */}
              <TouchableOpacity
                style={[
                  styles.optionCard,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={handleCSVImport}
              >
                <View style={styles.optionIcon}>
                  <Text style={[styles.optionIconText, { color: theme.colors.primary }]}>📄</Text>
                </View>
                <View style={styles.optionContent}>
                  <Text style={[styles.optionTitle, { color: theme.colors.text }]}>
                    {t('portfolio.uploadCsvFile', { defaultValue: 'Upload CSV File' })}
                  </Text>
                  <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                    {t('portfolio.uploadCsvDescription', {
                      defaultValue:
                        'Import positions and trades from Interactive Brokers CSV statement',
                    })}
                  </Text>
                </View>
              </TouchableOpacity>

              {/* Camera Option */}
              <TouchableOpacity
                style={[
                  styles.optionCard,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={handleCameraImport}
              >
                <View style={styles.optionIcon}>
                  <Text style={[styles.optionIconText, { color: theme.colors.primary }]}>📷</Text>
                </View>
                <View style={styles.optionContent}>
                  <Text style={[styles.optionTitle, { color: theme.colors.text }]}>
                    {t('portfolio.takePhoto', { defaultValue: 'Take Photo' })}
                  </Text>
                  <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                    {t('portfolio.takePhotoDescription', {
                      defaultValue: 'Take a photo of your portfolio statement',
                    })}
                  </Text>
                </View>
              </TouchableOpacity>

              {/* Gallery Option */}
              <TouchableOpacity
                style={[
                  styles.optionCard,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={handleImageImport}
              >
                <View style={styles.optionIcon}>
                  <Text style={[styles.optionIconText, { color: theme.colors.primary }]}>🖼️</Text>
                </View>
                <View style={styles.optionContent}>
                  <Text style={[styles.optionTitle, { color: theme.colors.text }]}>
                    {t('portfolio.selectFromGallery', { defaultValue: 'Select from Gallery' })}
                  </Text>
                  <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                    {t('portfolio.selectFromGalleryDescription', {
                      defaultValue: 'Upload up to 10 screenshots from your device',
                    })}
                  </Text>
                </View>
              </TouchableOpacity>

              {/* PDF Option */}
              <TouchableOpacity
                style={[
                  styles.optionCard,
                  {
                    backgroundColor: theme.colors.backgroundElevated,
                    borderColor: theme.colors.border,
                  },
                ]}
                onPress={handlePDFImport}
              >
                <View style={styles.optionIcon}>
                  <Text style={[styles.optionIconText, { color: theme.colors.primary }]}>📑</Text>
                </View>
                <View style={styles.optionContent}>
                  <Text style={[styles.optionTitle, { color: theme.colors.text }]}>
                    {t('portfolio.importFromPdf', { defaultValue: 'Import from PDF' })}
                  </Text>
                  <Text style={[styles.optionDescription, { color: theme.colors.textSecondary }]}>
                    {t('portfolio.importFromPdfDescription', {
                      defaultValue: 'Upload a PDF statement for AI-powered extraction',
                    })}
                  </Text>
                </View>
              </TouchableOpacity>
            </ScrollView>

            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: theme.colors.backgroundElevated }]}
              onPress={() => setShowModal(false)}
            >
              <Text style={[styles.cancelButtonText, { color: theme.colors.text }]}>
                {t('common.cancel')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    importButton: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      minHeight: 48,
      justifyContent: 'center',
    },
    importButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      borderTopLeftRadius: theme.radii.xl,
      borderTopRightRadius: theme.radii.xl,
      paddingTop: theme.spacing.lg,
      paddingHorizontal: theme.spacing.lg,
      paddingBottom: theme.spacing.lg,
      maxHeight: '80%',
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.lg,
    },
    modalTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    closeButton: {
      padding: theme.spacing.sm,
    },
    closeButtonText: {
      fontSize: theme.typography.fontSize3Xl,
      fontWeight: theme.typography.fontWeightLight,
    },
    optionsContainer: {
      marginBottom: theme.spacing.lg,
    },
    optionCard: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: theme.spacing.md,
      marginBottom: theme.spacing.md,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    optionIcon: {
      width: 48,
      height: 48,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.background,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: theme.spacing.md,
    },
    optionIconText: {
      fontSize: 24,
    },
    optionContent: {
      flex: 1,
    },
    optionTitle: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
    },
    optionDescription: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 20,
    },
    cancelButton: {
      paddingVertical: theme.spacing.md,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
